/**
 * errorReport — Push 4-1ai (2026-07-10) auto-notify-admin plumbing.
 *
 * Ean's frame: "'your admin has been notified' we set automatically in an
 * event log and notification." When a user hits a high-severity error
 * (5xx / network / OAuth failure), the SPA fires this helper in the
 * background. It posts to /api/me/errors/report which writes a
 * sys__event_log row (kind='user_error_reported') AND fans out a
 * notification to workspace admins. The user's toast says "Your admin has
 * been notified" so they know they're not alone.
 *
 * Client-side rate limit: identical error codes within a 60-second window
 * dedupe to prevent spamming admins on retries or hot-loop failures. Uses
 * an in-memory Map keyed by (error_code + surface). No localStorage per
 * repo hygiene (localStorage state races across tabs).
 *
 * Server-side rate limit + dedupe: enforced independently on the endpoint
 * (max 5 per user per hour + identical-triple dedup within 1h). This
 * client cache is just a fast-path so we don't burn the server-side quota
 * on hot loops.
 *
 * Fire-and-forget contract: this helper NEVER throws. If the network POST
 * fails, we silently swallow — the ErrorBoundary handles catastrophic UI
 * failures separately (via /api/client-error-report). This endpoint is
 * for the softer "user hit a 500 in a normal API call" case where we
 * still want admins to know but don't want to escalate the user's own
 * error flow.
 */

interface ReportErrorContext {
  /** UI surface where the error surfaced — 'chat' | 'connector_install' | ... */
  surface?: string;
  /** Short-id of the record involved (connector, prompt, model, folder) if applicable. */
  recordShortId?: string;
  /** The /api/* path that failed. Populated automatically when the caller
   *  passes an ApiError; can be overridden here for non-ApiError throws. */
  endpoint?: string;
}

interface ReportErrorResult {
  notified: boolean;
  dedup: boolean;
}

/**
 * Client-side dedup cache. Keyed by `${errorCode}|${surface}` so a single
 * error code hitting different surfaces (chat, admin, settings) still
 * fires distinct reports. Entries expire after 60s.
 */
const clientDedup = new Map<string, number>();
const DEDUP_WINDOW_MS = 60_000;

function pruneDedup(now: number): void {
  for (const [key, ts] of clientDedup) {
    if (now - ts > DEDUP_WINDOW_MS) {
      clientDedup.delete(key);
    }
  }
}

/**
 * Extract a normalized error code from an unknown throw. ApiError has an
 * explicit `code` field (server-supplied); non-ApiError throws map to
 * synthetic codes so the dedup cache still works.
 */
function extractErrorCode(err: unknown): string {
  if (err !== null && typeof err === 'object') {
    const e = err as { name?: string; code?: string | null; status?: number };
    if (e.name === 'ApiError') {
      if (e.code !== null && e.code !== undefined && e.code !== '') return e.code;
      if (typeof e.status === 'number') return `http_${e.status}`;
    }
    if (e.name === 'TypeError') return 'client_network_failure';
  }
  return 'unknown_client_error';
}

function extractErrorMessage(err: unknown): string {
  if (err !== null && typeof err === 'object') {
    const e = err as { message?: string; friendly?: string };
    if (typeof e.friendly === 'string' && e.friendly !== '') return e.friendly;
    if (typeof e.message === 'string' && e.message !== '') return e.message;
  }
  return String(err ?? 'unknown error');
}

/**
 * Fire a report to /api/me/errors/report. Fire-and-forget; NEVER throws.
 *
 * Returns `{notified: true, dedup: false}` when a fresh report was fired,
 * `{notified: true, dedup: true}` when the client-side cache dedup'd it
 * (from the user's POV it's "already reported"), or
 * `{notified: false, dedup: false}` when the network call failed.
 *
 * Callers typically don't read the return value — this is fired from
 * showApiErrorToast as a background task via `void reportError(err)`.
 * Return value is exposed for tests + surfaces that want to render
 * "Reported" state after a manual "Notify admin" click.
 */
export async function reportError(
  err: unknown,
  context: ReportErrorContext = {},
): Promise<ReportErrorResult> {
  try {
    const code = extractErrorCode(err);
    const surface = context.surface ?? 'unknown';
    const now = Date.now();
    pruneDedup(now);

    const key = `${code}|${surface}`;
    const last = clientDedup.get(key);
    if (last !== undefined && now - last < DEDUP_WINDOW_MS) {
      return { notified: true, dedup: true };
    }
    clientDedup.set(key, now);

    const endpoint =
      context.endpoint ??
      (err !== null && typeof err === 'object' && 'endpoint' in err
        ? String((err as { endpoint?: unknown }).endpoint ?? '')
        : '');

    const body = {
      error_code: code,
      message: extractErrorMessage(err),
      context: {
        surface: context.surface ?? null,
        record_short_id: context.recordShortId ?? null,
        endpoint: endpoint !== '' ? endpoint : null,
      },
    };

    const res = await fetch('/api/me/errors/report', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      // Silent failure — reporting the error-report failure would be a
      // logic loop. If the endpoint itself is 500ing, the operator-side
      // sys__operator_alert path (from other endpoints) will surface it.
      return { notified: false, dedup: false };
    }

    const parsed = (await res.json().catch(() => null)) as
      | { ok?: boolean; dedup?: boolean }
      | null;
    return {
      notified: parsed?.ok === true,
      dedup: parsed?.dedup === true,
    };
  } catch {
    // Fetch threw (offline, aborted, TLS) — swallow silently.
    return { notified: false, dedup: false };
  }
}

/**
 * Predicate — should this error fire an auto-notify to admins?
 *
 * Yes for: 500/502/503/504, network failures (status=0), OAuth refresh
 *   failures (code='OAUTH_REQUIRED' + repeated), 403 (user hit a
 *   permission wall admin should know about).
 *
 * No for: 402 (out of credits — normal usage state, has its own top-up
 *   flow), 429 (rate limited — expected pressure signal), 401 (session
 *   expiry — handled by SessionExpiryListener), 404 (record missing —
 *   normal state).
 *
 * Callers can override with `{skipAutoNotify: true}` on showApiErrorToast
 * for expected error paths (e.g. optimistic UI that expects 404 on a
 * background poll).
 */
export function shouldAutoNotifyAdmin(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const e = err as { name?: string; status?: number; code?: string | null };
  if (e.name !== 'ApiError') return false;
  const status = e.status;
  if (typeof status !== 'number') return false;
  // 5xx → yes
  if (status >= 500 && status < 600) return true;
  // Network failure (status=0, code='network_failure')
  if (status === 0) return true;
  // 403 forbidden → admin should know user hit a wall
  if (status === 403) return true;
  return false;
}
